import type { ExpandedState, SortingState, Updater } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useShallow } from "zustand/react/shallow"

import { CUSTOMER_ORDER_FILTER_KEYS, CUSTOMER_ORDER_ROUTE } from "../constants"
import type { CustomerOrderSearchParams } from "../schemas"
import { useCustomerOrderSearchStore } from "../stores/useCustomerOrderSearchStore"

import type { CustomerLocationCombination } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseCustomerOrderListReturn {
  customerOrders: CustomerLocationCombination[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  expanded: ExpandedState
  searchParams: CustomerOrderSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleExpandedChange: (updater: Updater<ExpandedState>) => void
  handleSearchChange: (query: string) => void
  handleScroll: () => void
  handleFilterChange: <K extends keyof CustomerOrderSearchParams>(key: K, value: CustomerOrderSearchParams[K]) => void
  handleFiltersChange: (updates: Partial<CustomerOrderSearchParams>) => void
  handleFilterRemove: (
    keyOrKeys: keyof CustomerOrderSearchParams | (keyof CustomerOrderSearchParams)[],
    value?: string | number
  ) => void
  handleFilterClear: () => void
}

export const useCustomerOrderList = (): UseCustomerOrderListReturn => {
  const store = useCustomerOrderSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: customerOrders,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  } = useListPageBase<CustomerLocationCombination, CustomerOrderSearchParams>({
    route: CUSTOMER_ORDER_ROUTE,
    store,
    filterKeys: CUSTOMER_ORDER_FILTER_KEYS,
  })

  return {
    customerOrders,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
